import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useEditMode,
  useSlotGroup,
} from '@vibesites/runtime/react';

interface SpecialOffer {
  title?: string | null;
  description?: string | null;
  disclaimer?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  enabled?: string | null;
  [key: string]: unknown;
}

/** Per-tab session key: once auto-shown for a fingerprint, don't auto-open again. */
const POPUP_SESSION_KEY = 'sob-popup-seen';
const POPUP_AUTO_OPEN_DELAY_MS = 700;

const DEFAULT_DISCLAIMER =
  'Conditions may apply. Contact leasing for more details.';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Popup CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function hasValue(v: unknown): v is string {
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== '-' && s !== 'null' && s !== 'undefined' && s !== 'false';
}

/**
 * Parse offer start/end dates. Date-only values (YYYY-MM-DD) use local day
 * bounds so an end date of "today" stays active through the end of that day.
 */
function parseOfferDate(value: string, bound: 'start' | 'end'): number | null {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    if (!y || !m || !d) return null;
    if (bound === 'end') return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
    return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
  }
  const t = new Date(trimmed).getTime();
  return Number.isNaN(t) ? null : t;
}

function isOfferActive(offer: SpecialOffer | null | undefined): boolean {
  if (!offer) return false;

  // NOTE: we intentionally do NOT consult `offer.enabled`. In the data it
  // is a static, always-`false` default, so honoring it would hide every
  // offer. Visibility is driven solely by content + start/end date window.

  if (!hasValue(offer.title) && !hasValue(offer.description)) return false;

  const now = Date.now();

  if (hasValue(offer.start_date)) {
    const start = parseOfferDate(offer.start_date!, 'start');
    if (start != null && start > now) return false;
  }

  if (hasValue(offer.end_date)) {
    const end = parseOfferDate(offer.end_date!, 'end');
    if (end != null && end < now) return false;
  }

  return true;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function setBannerHeight(px: number) {
  document.documentElement.style.setProperty('--offer-banner-height', `${px}px`);
}

/** Fingerprint active offers so a content change re-shows the popup. */
function offersFingerprint(offers: SpecialOffer[]): string {
  return offers
    .map(
      (o) =>
        `${o.title ?? ''}|${o.description ?? ''}|${o.start_date ?? ''}|${o.end_date ?? ''}`,
    )
    .join('||');
}

function readPopupSeen(): string | null {
  try {
    return sessionStorage.getItem(POPUP_SESSION_KEY);
  } catch {
    return null;
  }
}

function writePopupSeen(fingerprint: string) {
  try {
    sessionStorage.setItem(POPUP_SESSION_KEY, fingerprint);
  } catch {
    // private mode / blocked storage — ignore
  }
}

// ============================================================================
// Auto-rotating carousel for multiple offers
// ============================================================================

function useOfferRotation(count: number, intervalMs = 6000) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (count <= 1 || paused) return;
    const id = setInterval(() => setActive(i => (i + 1) % count), intervalMs);
    return () => clearInterval(id);
  }, [count, intervalMs, paused]);

  const goTo = useCallback((i: number) => setActive(i), []);
  const pause = useCallback(() => setPaused(true), []);
  const resume = useCallback(() => setPaused(false), []);

  return { active, goTo, pause, resume };
}

// ============================================================================
// Component
// ============================================================================

export function SpecialOfferBanner() {
  const offers = useCollectionBinding<SpecialOffer>('specialofferbanner.items', {
    collection: 'special_offers',
  });
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;
  const ctaLink = useSlotGroup('specialofferbanner.cta', ctaLinkDef);
  const defaultDisclaimer = useContentToken('specialofferbanner.default_disclaimer');

  const [dismissed, setDismissed] = useState(false);
  const [scrolledAway, setScrolledAway] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const measuredHeight = useRef(0);
  // Always-fresh ref so the ResizeObserver callback can read the latest
  // isVisible value without needing to be recreated on every visibility change.
  const isVisibleRef = useRef(false);
  const editAutoOpened = useRef(false);

  const activeOffers = useMemo(() => {
    const all = offers || [];
    return all.filter(isOfferActive);
  }, [offers]);

  const fingerprint = useMemo(() => offersFingerprint(activeOffers), [activeOffers]);
  const hasOffers = activeOffers.length > 0;
  const isVisible = hasOffers && animateIn && !dismissed && !scrolledAway;
  const multi = activeOffers.length > 1;

  const { active: activeIdx, goTo, pause, resume } = useOfferRotation(activeOffers.length);

  // Keep the ref in sync so the ResizeObserver can read the latest value.
  useEffect(() => { isVisibleRef.current = isVisible; }, [isVisible]);

  // Measure natural banner height and recompute on every resize (e.g. viewport
  // width change that causes text to reflow to a different height).
  useEffect(() => {
    if (!hasOffers || !bannerRef.current) {
      setBannerHeight(0);
      return;
    }

    const el = bannerRef.current;

    const measure = () => {
      const h = el.scrollHeight;
      measuredHeight.current = h;
      // Re-sync the CSS variable immediately so the header repositions.
      setBannerHeight(isVisibleRef.current ? h : 0);
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasOffers, activeIdx]);

  // Animate in
  useEffect(() => {
    if (!hasOffers) { setBannerHeight(0); return; }
    const raf = requestAnimationFrame(() => setAnimateIn(true));
    return () => cancelAnimationFrame(raf);
  }, [hasOffers]);

  // Sync CSS variable when visibility toggles (dismiss / scroll-away / restore).
  useEffect(() => {
    setBannerHeight(isVisible ? measuredHeight.current : 0);
  }, [isVisible]);

  useEffect(() => () => setBannerHeight(0), []);

  // Scroll-based auto-hide with hysteresis to prevent bouncing
  useEffect(() => {
    if (!hasOffers || dismissed) return;
    let ticking = false;
    const HIDE_AT = 200;
    const SHOW_AT = 50;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolledAway(prev => {
          if (!prev && y > HIDE_AT) return true;
          if (prev && y < SHOW_AT) return false;
          return prev;
        });
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasOffers, dismissed]);

  const [modalOpen, setModalOpen] = useState(false);

  const handleDismiss = useCallback(() => setDismissed(true), []);
  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    // Live site: remember so SPA navigations / reloads in this tab don't
    // auto-open again. Editor: never write — reload must re-show for editing.
    if (!isEditMode && fingerprint) writePopupSeen(fingerprint);
  }, [fingerprint, isEditMode]);

  // Live site: auto-open only when ≥1 in-date special exists, once per tab
  // session for that offer set (sessionStorage). Mark seen on show so
  // navigating between pages that mount this banner doesn't keep reopening.
  // Editor: open once on load/reload so disclaimer + CTA are editable — ignore
  // sessionStorage and don't require active offers.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isEditMode) {
      if (editAutoOpened.current) return;
      const raf = requestAnimationFrame(() => {
        editAutoOpened.current = true;
        setModalOpen(true);
      });
      return () => cancelAnimationFrame(raf);
    }

    if (!hasOffers || !fingerprint) return;
    if (readPopupSeen() === fingerprint) return;

    const id = window.setTimeout(() => {
      writePopupSeen(fingerprint);
      setModalOpen(true);
    }, POPUP_AUTO_OPEN_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [isEditMode, hasOffers, fingerprint]);

  // Escape closes the popup; lock body scroll while open.
  useEffect(() => {
    if (!modalOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [modalOpen, closeModal]);

  // Editor wrappers (EditableSection / EditableCollectionBinding) use React
  // onClickCapture and follow the React tree — they steal X/backdrop clicks
  // and open settings instead of dismissing. Native document capture runs
  // first and stopImmediatePropagation so those handlers never see the event.
  useEffect(() => {
    if (!modalOpen) return;

    const shouldDismiss = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      if (target.closest('.sob-modal-close')) return true;
      // Backdrop only — not clicks inside the dialog panel.
      return target.classList.contains('sob-modal-overlay');
    };

    const onPointerDownCapture = (e: PointerEvent) => {
      if (!shouldDismiss(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      closeModal();
    };
    const onClickCapture = (e: MouseEvent) => {
      if (!shouldDismiss(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    document.addEventListener('pointerdown', onPointerDownCapture, true);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [modalOpen, closeModal]);

  // Keep the section mounted in the editor even with no active offers so the
  // popup (disclaimer / CTA / collection) can still be edited.
  if (!hasOffers && !isEditMode) return null;

  const offer = activeOffers[activeIdx] || activeOffers[0];
  const showReopenButton = dismissed || scrolledAway || (isEditMode && !modalOpen);
  const title = hasValue(offer?.title) ? offer!.title! : '';
  const description = hasValue(offer?.description) ? stripHtml(offer!.description!) : '';
  const disclaimer = hasValue(offer?.disclaimer) ? stripHtml(String(offer!.disclaimer)) : '';

  return (
    <>
    <EditableCollectionBinding
      bindingPath="specialofferbanner.items"
      label="Special Offers"
    >
      <div
        ref={bannerRef}
        className="sob"
        data-visible={isVisible}
        role="banner"
        aria-label="Special offers"
        onMouseEnter={pause}
        onMouseLeave={resume}
      >
        {/* Shimmer accent */}
        <div className="sob-shimmer" />

        <div className="sob-inner">
          {/* Offer content — crossfade between offers */}
          <div className="sob-content">
            <div className="sob-text" key={activeIdx}>
              {title && <p className="sob-title">{title}</p>}
              {description && <p className="sob-desc">{description}</p>}
              {disclaimer && <p className="sob-disclaimer">{disclaimer}</p>}
            </div>

            {/* Dot indicators — centered below content */}
            {multi && (
              <div className="sob-dots" role="tablist" aria-label="Offers">
                {activeOffers.map((_, i) => (
                  <button
                    key={i}
                    className={`sob-dot${i === activeIdx ? ' sob-dot--active' : ''}`}
                    onClick={() => goTo(i)}
                    role="tab"
                    aria-selected={i === activeIdx}
                    aria-label={`Offer ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Close */}
          <button onClick={handleDismiss} className="sob-close" aria-label="Dismiss">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Auto-rotation progress bar */}
        {multi && isVisible && (
          <div className="sob-progress">
            <div className="sob-progress-bar" key={activeIdx} />
          </div>
        )}
      </div>

      {/* Floating pill — opens modal */}
      <button
        onClick={openModal}
        className="sob-reopen"
        style={{
          opacity: showReopenButton ? 1 : 0,
          pointerEvents: showReopenButton ? 'auto' : 'none',
          transform: showReopenButton
            ? 'translateX(-50%) translateY(0)'
            : 'translateX(-50%) translateY(16px)',
        }}
        aria-label="Show special offers"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <span>{activeOffers.length > 1 ? `${activeOffers.length} Special Offers` : 'Special Offer'}</span>
      </button>
    </EditableCollectionBinding>

      {/* Modal — outside EditableCollectionBinding; dismiss via native
          document capture (see effect) so editor click-capture can't steal it. */}
      {modalOpen && (() => {
        const mDisclaimer =
          (hasValue(defaultDisclaimer) ? String(defaultDisclaimer).trim() : '') ||
          DEFAULT_DISCLAIMER;

        return (
          <div
            className="sob-modal-overlay"
            role="presentation"
          >
            <div
              className="sob-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sob-modal-title"
            >
              <div className="sob-modal-header">
                <h3 id="sob-modal-title" className="sob-modal-title">
                  {multi
                    ? 'Special Offers'
                    : hasValue(activeOffers[0]?.title)
                      ? activeOffers[0].title
                      : 'Special Offer'}
                </h3>
                <button
                  type="button"
                  className="sob-modal-close"
                  aria-label="Close"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="sob-modal-body">
                <div className="sob-modal-offers">
                  {activeOffers.map((modalOffer, i) => {
                    const mTitle = hasValue(modalOffer.title) ? modalOffer.title : '';
                    const mDesc = hasValue(modalOffer.description)
                      ? stripHtml(modalOffer.description)
                      : '';
                    if (!mTitle && !mDesc) return null;
                    return (
                      <div
                        key={`${mTitle}-${i}`}
                        className={`sob-modal-offer${i > 0 ? ' sob-modal-offer--divided' : ''}`}
                      >
                        {multi && mTitle ? (
                          <h4 className="sob-modal-offer-title">{mTitle}</h4>
                        ) : null}
                        {mDesc ? <p className="sob-modal-desc">{mDesc}</p> : null}
                      </div>
                    );
                  })}
                </div>

                <EditableToken
                  path="specialofferbanner.default_disclaimer"
                  as="p"
                  className="sob-modal-disclaimer"
                >
                  {mDisclaimer}
                </EditableToken>

                <EditableSlotGroup
                  groupPath="specialofferbanner.cta"
                  definition={ctaLinkDef}
                  className="sob-modal-cta-wrap"
                >
                  <a
                    href={ctaLink.url || '/contact'}
                    target={ctaLink._target || '_self'}
                    className="sob-modal-cta group/cta"
                    onClick={closeModal}
                  >
                    <span className="relative z-10">{ctaLink.label || 'Contact Us'}</span>
                    <svg
                      className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                      />
                    </svg>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`
        /* ================================================================ */
        /* Banner shell                                                     */
        /* ================================================================ */
        .sob {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 60;
          background: linear-gradient(
            100deg,
            color-mix(in srgb, var(--vibe-color-primary) 92%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 72%, black) 100%
          );
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                      opacity 0.4s ease;
        }
        .sob[data-visible="true"] {
          max-height: 140px;
          opacity: 1;
        }

        /* Subtle shimmer sweep across the banner */
        .sob-shimmer {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            110deg,
            transparent 20%,
            rgba(255,255,255,0.06) 40%,
            rgba(255,255,255,0.06) 60%,
            transparent 80%
          );
          background-size: 200% 100%;
          animation: sob-shimmer 6s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes sob-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        /* ================================================================ */
        /* Inner layout                                                     */
        /* ================================================================ */
        .sob-inner {
          position: relative;
          max-width: 80rem;
          margin: 0 auto;
          padding: 0.65rem 3rem 0.65rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        /* ================================================================ */
        /* Text content                                                     */
        /* ================================================================ */
        .sob-content {
          flex: 1;
          min-width: 0;
          text-align: center;
        }

        .sob-text {
          animation: sob-fade-in 0.4s ease both;
        }
        @keyframes sob-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .sob-title {
          font-family: var(--vibe-font-heading);
          font-size: 0.9rem;
          font-weight: 700;
          color: var(--vibe-color-text-on-primary);
          letter-spacing: 0.02em;
          margin: 0;
          line-height: 1.3;
        }

        .sob-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 80%, transparent);
          margin: 0.15rem 0 0;
          line-height: 1.4;
        }

        .sob-disclaimer {
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          font-style: italic;
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 50%, transparent);
          margin: 0.15rem 0 0;
          line-height: 1.3;
        }

        /* ================================================================ */
        /* Dot indicators                                                   */
        /* ================================================================ */
        .sob-dots {
          display: flex;
          justify-content: center;
          gap: 6px;
          margin-top: 0.35rem;
        }
        .sob-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          border: 1.5px solid color-mix(in srgb, var(--vibe-color-text-on-primary) 50%, transparent);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .sob-dot:hover {
          border-color: var(--vibe-color-text-on-primary);
          transform: scale(1.2);
        }
        .sob-dot--active {
          background: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-text-on-primary);
        }

        /* ================================================================ */
        /* Close button                                                     */
        /* ================================================================ */
        .sob-close {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px; height: 26px;
          border-radius: 50%;
          border: 1.5px solid color-mix(in srgb, var(--vibe-color-text-on-primary) 25%, transparent);
          background: transparent;
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 70%, transparent);
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.2s;
        }
        .sob-close:hover {
          background: color-mix(in srgb, var(--vibe-color-text-on-primary) 15%, transparent);
          color: var(--vibe-color-text-on-primary);
          border-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 50%, transparent);
          transform: translateY(-50%) scale(1.1);
        }

        /* ================================================================ */
        /* Auto-rotation progress bar                                       */
        /* ================================================================ */
        .sob-progress {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 2px;
          background: rgba(255,255,255,0.1);
        }
        .sob-progress-bar {
          height: 100%;
          background: color-mix(in srgb, var(--vibe-color-text-on-primary) 40%, transparent);
          animation: sob-progress-fill 6s linear forwards;
        }
        @keyframes sob-progress-fill {
          from { width: 0; }
          to   { width: 100%; }
        }

        /* ================================================================ */
        /* Floating reopen pill                                             */
        /* ================================================================ */
        .sob-reopen {
          position: fixed;
          bottom: 1.25rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 90;
          display: flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.6rem 1.25rem;
          border-radius: 9999px;
          border: none;
          background: linear-gradient(
            100deg,
            color-mix(in srgb, var(--vibe-color-primary) 92%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 72%, black) 100%
          );
          color: var(--vibe-color-text-on-primary);
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.025em;
          cursor: pointer;
          box-shadow: 0 4px 20px rgba(0,0,0,0.25);
          transition: opacity 0.35s ease, transform 0.35s ease, box-shadow 0.2s;
        }
        .sob-reopen:hover {
          box-shadow: 0 6px 24px rgba(0,0,0,0.35);
          transform: translateX(-50%) translateY(-3px) !important;
        }

        /* ================================================================ */
        /* Responsive                                                       */
        /* ================================================================ */
        @media (max-width: 640px) {
          .sob-inner { padding: 0.6rem 2.5rem 0.6rem 0.75rem; gap: 0.5rem; }
          .sob-title { font-size: 0.82rem; }
          .sob-desc { font-size: 0.75rem; }
          .sob-dots { gap: 5px; }
          .sob-dot { width: 6px; height: 6px; }
        }

        /* ================================================================ */
        /* Modal                                                            */
        /* ================================================================ */
        .sob-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0);
          animation: sob-overlay-in 0.3s ease forwards;
        }
        @keyframes sob-overlay-in {
          to { background: rgba(0, 0, 0, 0.5); }
        }

        .sob-modal {
          position: relative;
          width: min(92vw, 720px);
          background: #fff;
          border: 1px solid #d0d0d0;
          border-radius: 0;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
          opacity: 0;
          transform: translateY(16px);
          animation: sob-modal-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) 0.05s forwards;
        }
        @keyframes sob-modal-in {
          to { opacity: 1; transform: translateY(0); }
        }

        .sob-modal-header {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.35rem 3rem 1.25rem;
          border-bottom: 1px solid #d8d8d8;
        }

        .sob-modal-title {
          font-family: var(--vibe-font-body);
          font-size: clamp(1.35rem, 2.4vw, 1.75rem);
          font-weight: 700;
          color: var(--vibe-color-primary);
          margin: 0;
          line-height: 1.25;
          text-align: center;
        }

        .sob-modal-close {
          position: absolute;
          top: 50%;
          right: 0.5rem;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          padding: 0;
          border: none;
          border-radius: 0;
          background: transparent;
          color: #8a8a8a;
          cursor: pointer;
          transition: color 0.2s;
          z-index: 2;
        }
        .sob-modal-close:hover {
          color: #222;
        }

        .sob-modal-body {
          padding: 2.25rem 2.5rem 1.75rem;
          text-align: center;
          animation: sob-fade-in 0.3s ease both;
        }

        .sob-modal-desc {
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          font-weight: 400;
          color: #111;
          margin: 0;
          line-height: 1.55;
        }

        .sob-modal-offers {
          display: flex;
          flex-direction: column;
          gap: 0;
          max-height: min(50vh, 28rem);
          overflow-y: auto;
          margin-bottom: 1rem;
        }

        .sob-modal-offer {
          padding: 0 0 1rem;
        }
        .sob-modal-offer--divided {
          padding-top: 1rem;
          border-top: 1px solid #e5e5e5;
        }
        .sob-modal-offer:last-child {
          padding-bottom: 0;
        }

        .sob-modal-offer-title {
          font-family: var(--vibe-font-body);
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          margin: 0 0 0.5rem;
          line-height: 1.3;
        }

        .sob-modal-disclaimer {
          font-family: var(--vibe-font-body);
          font-size: 0.82rem;
          font-style: italic;
          font-weight: 400;
          color: #111;
          margin: 0;
          line-height: 1.5;
        }

        .sob-modal-cta-wrap {
          display: flex;
          justify-content: center;
          margin-top: 2rem;
        }

        .sob-modal-cta {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          padding: 0.875rem 2rem;
          overflow: hidden;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          text-decoration: none;
          color: var(--vibe-color-text-on-primary);
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%
          );
          border-radius: 9999px;
          box-shadow:
            0 1px 3px rgba(0, 0, 0, 0.12),
            0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
          transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
        }
        .sob-modal-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.15) 50%,
            transparent 100%
          );
          transition: left 0.5s ease;
        }
        .sob-modal-cta:hover {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%,
            var(--vibe-color-primary) 100%
          );
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          transform: translateY(-1px);
        }
        .sob-modal-cta:hover::before {
          left: 100%;
        }
        .sob-modal-cta:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        }

        @media (max-width: 640px) {
          .sob-modal-header { padding: 1.1rem 2.5rem 1rem; }
          .sob-modal-body { padding: 1.75rem 1.25rem 1.35rem; }
          .sob-modal-desc { font-size: 0.95rem; }
          .sob-modal-disclaimer { font-size: 0.75rem; }
        }

        /* ================================================================ */
        /* Push page content down by banner height so hero moves with nav  */
        /* ================================================================ */
        .vibesites-main {
          padding-top: var(--offer-banner-height, 0px);
          transition: padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @media (prefers-reduced-motion: reduce) {
          .sob, .sob-reopen, .sob-text { transition: none; animation: none; }
          .sob-shimmer { animation: none; }
          .sob-progress-bar { animation: none; width: 100%; }
          .sob-modal-overlay, .sob-modal, .sob-modal-body { animation: none; opacity: 1; transform: none; background: rgba(0,0,0,0.5); }
          .sob-modal-cta { transition: none; }
          .vibesites-main { transition: none; }
        }
      `}</style>

      {/* Keep schema wrappers mounted while the popup is closed (edit mode / lint). */}
      {!modalOpen && (
        <div className="hidden" style={{ display: 'none' }} aria-hidden="true">
          <EditableToken path="specialofferbanner.default_disclaimer" as="span">
            {''}
          </EditableToken>
          <EditableSlotGroup groupPath="specialofferbanner.cta" definition={ctaLinkDef}>
            <span />
          </EditableSlotGroup>
        </div>
      )}
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialOfferBanner',
  displayName: 'Special Offer Banner',
  tokens: {
    'specialofferbanner.default_disclaimer': {
      label: 'Disclaimer',
      type: 'text',
      defaultValue: 'Conditions may apply. Contact leasing for more details.',
    },
  },
  slotGroups: {
    'specialofferbanner.cta': ctaLinkDef,
  },
  collectionBindings: {
    'specialofferbanner.items': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(SpecialOfferBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SpecialOfferBanner;
